import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core/utils/cn';
import { Badge } from '@/components/ui/badge';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * "product" — shows badge / brand / product name / price (New In, Trending Now).
 * "category" — shows label and "Shop now" link text (By Category, By Room, etc.).
 */
export type ShopMiniCardVariant = 'product' | 'category';

export interface ShopMiniCardProps {
  variant: ShopMiniCardVariant;
  /** CMS/Shopify image URL. TODO: replace with ImageWithCrops when CMS wires up. */
  image?: string;
  /** Small badge above brand name — e.g. "New Season", "Trending now". */
  badge?: string;
  /** Brand name displayed in uppercase semibold — product variant only. */
  brand?: string;
  /** Product or subcategory name — product variant only. */
  name?: string;
  /** Formatted price string — product variant only. */
  price?: string;
  /** Category/subcategory label — category variant only. */
  label?: string;
  onClick?: () => void;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ShopMiniCard({
  variant,
  image,
  badge,
  brand,
  name,
  price,
  label,
  onClick,
  className,
}: ShopMiniCardProps) {
  const t = useClientTranslation();

  return (
    <article
      className={cn('flex w-[244px] shrink-0 cursor-pointer flex-col gap-2', className)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
    >
      {/* Image */}
      <div className="bg-jncquoi-neutral-50 relative h-[320px] w-full overflow-hidden">
        {image && (
          <img
            src={image}
            alt={label ?? brand ?? name ?? ''}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>

      {/* Details */}
      {variant === 'product' ? (
        <div className="flex flex-col gap-2">
          {badge && <Badge>{badge}</Badge>}
          <div className="flex flex-col">
            {brand && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-tight font-semibold tracking-[0.5px] uppercase">
                {brand}
              </span>
            )}
            {name && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-tight font-normal tracking-[0.5px]">
                {name}
              </span>
            )}
            {price && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[14px] leading-tight font-normal tracking-[0.5px]">
                {price}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {label && (
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold tracking-[0.5px] uppercase">
              {label}
            </span>
          )}
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-none font-normal tracking-[0.25px]">
            {t('App.Shop.ShopNow')}
          </span>
        </div>
      )}
    </article>
  );
}
